import { Divider } from "antd";
import React from "react";


const CDN_TEAM_LOGO = (id) =>
    id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined;

const ORDER = [
    "Ball Possession",
    "Total Shots",
    "Shots on Goal",
    "Shots off Goal",
    "Blocked Shots",
    "Shots insidebox",
    "Shots outsidebox",
    "Total passes",
    "Passes accurate",
    "Passes %",
    "Corner Kicks",
    "Goalkeeper Saves",
    "Tackles",
    "Fouls",
    "Offsides",
    "Counter Attacks",
    "Yellow Cards",
    "Red Cards",
];

const toNumber = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return v;
    const s = String(v).trim();
    if (s.endsWith("%")) return Number(s.slice(0, -1));
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
};


export default function MatchStats({
    fixture,
    pairs,
    home = { id: null, name: "Home", color: "#2563eb" }, 
    away = { id: null, name: "Away", color: "#16a34a" }, 
    rapidKey = process.env.REACT_APP_RAPIDAPI_KEY,
}) {

    return (
        <section className="px-container">
            <div className="ms-wrap">
                <style>{css}</style>
                <h5 className="section-title mb-0">
                    {fixture?.teams?.home?.name} vs {fixture?.teams?.away?.name} — Match Statistics
                </h5>
                <p className="section-subtitle mb-3">
                    Live breakdown of possession, shots, passes and cards. Bars fill from the center to compare teams at a glance.
                </p>

                <div className="w-100" style={{ backgroundColor: '#fff', padding: 20 }}>
                    <div className="ms-head">
                        <div className="ms-team">
                            <span className="ms-dot" style={{ background: home.color }} />
                            <img src={CDN_TEAM_LOGO(home.id)} alt="" aria-hidden="true" />
                            <span className="ms-name">{home.name}</span>
                        </div>
                        <div className="ms-team">
                            <span className="ms-dot" style={{ background: away.color }} />
                            <img src={CDN_TEAM_LOGO(away.id)} alt="" aria-hidden="true" />
                            <span className="ms-name">{away.name}</span>
                        </div>
                    </div>
                    <div className="w-100">
                        <Divider className="w-100 mt-2 mb-2" />
                    </div>

                    {pairs && (
                        <div className="ms-table">

                            {ORDER.map((label) => {
                                const lRaw = pairs.left.map.get(label);
                                const rRaw = pairs.right.map.get(label);

                                // treat strings ending with % as percent-based
                                const lIsPct = typeof lRaw === "string" && lRaw.trim().endsWith("%");
                                const rIsPct = typeof rRaw === "string" && rRaw.trim().endsWith("%");

                                const lVal = toNumber(lRaw);
                                const rVal = toNumber(rRaw);

                                let lPct, rPct, showL = lRaw ?? 0, showR = rRaw ?? 0;

                                if (lIsPct || rIsPct || label === "Ball Possession" || label.includes("%")) {
                                    const lValNum = Math.max(0, Math.min(100, lVal));
                                    const rValNum = Math.max(0, Math.min(100, rVal));

                                    if (lValNum === 0 && rValNum === 0) {
                                        lPct = 0;
                                        rPct = 0;
                                    } else {
                                        lPct = lValNum;
                                        rPct = (rRaw !== undefined && rRaw !== null) ? rValNum : (100 - lPct);
                                    }

                                    showL = `${lValNum}%`;
                                    showR = `${rValNum}%`;
                                } else {
                                    const total = lVal + rVal;
                                    if (total === 0) {
                                        lPct = 0;
                                        rPct = 0;
                                    } else {
                                        lPct = (lVal / total) * 100;
                                        rPct = 100 - lPct;
                                    }
                                    showL = lVal;
                                    showR = rVal;
                                }

                                return (
                                    <div className="ms-row" key={label}>
                                        <div className="ms-values">
                                            <span className="ms-left">
                                                {showL}
                                            </span>
                                            <span className="ms-label">{label}</span>
                                            <span className="ms-right">
                                                {showR}
                                            </span>
                                        </div>

                                        <div className="ms-bar">
                                            <div className="ms-half ms-half--left">
                                                {lPct > 0 && (
                                                    <div
                                                        className="ms-fill"
                                                        style={{ width: `${lPct}%`, background: pairs.left.color }}
                                                    />
                                                )}
                                            </div>

                                            <div className="ms-half ms-half--right">
                                                {rPct > 0 && (
                                                    <div
                                                        className="ms-fill"
                                                        style={{ width: `${rPct}%`, background: pairs.right.color }}
                                                    />
                                                )}
                                            </div>

                                            <span className="ms-mid" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

const css = `
.px-container { background:#f7f7f9; padding:30px; }
@media (max-width: 520px) { .px-container { padding:16px; } }
.ms-wrap {
  max-width: 1380px; margin: 0 auto;
}
.ms-head {
  display: grid !important;
  grid-template-columns: minmax(0,1fr) minmax(0,1fr);
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

/* Home (left) stays left; Away (right) stays right */
.ms-head .ms-team:first-child { justify-self: start; text-align: left; }
.ms-head .ms-team:last-child  { justify-self: end;   text-align: right; }

/* Make the team blocks resilient on narrow screens */
.ms-team {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;              /* allow truncation */
  overflow: hidden;
}

.ms-name {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;   /* no wrapping, ellipsis instead */
  white-space: nowrap;
}

.ms-team img { width: 18px; height: 18px; object-fit: contain; }
.ms-dot      { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }

/* Compact on very small phones but keep split */
@media (max-width: 420px) {
  .ms-team img { width: 16px; height: 16px; }
  .ms-name     { font-size: 13px; }
}

/* IMPORTANT: kill the old mobile rule that stacked the teams */
@media (max-width: 560px) {
  .ms-head {
    display: grid !important;
    grid-template-columns: minmax(0,1fr) minmax(0,1fr);
  }
}

.ms-table { display:flex; flex-direction:column; gap:10px; }
.ms-rail { display:flex; justify-content:space-between; color:#94a3b8; font-size:12px; }
.ms-rail-mid { color:#64748b; font-weight:700; }

.ms-row { display:flex; flex-direction:column; gap:6px; }
.ms-values { display:grid; grid-template-columns: 1fr auto 1fr; align-items:center; }
.ms-left { justify-self:start; font-weight:500; }
.ms-label { color:#475569; font-weight:700; }
.ms-right { justify-self:end; font-weight:500; }

.ms-bar {
  position: relative;
  height: 10px;
  border-radius: 6px;
  background: #eef2f7;
  overflow: hidden;
}

/* Each half is 50% wide; center split is the middle */
.ms-half {
  position: absolute;
  top: 0;
  height: 100%;
  width: 50%;
}

/* Left half occupies the left side; right half the right side */
.ms-half--left { left: 0; }
.ms-half--right { right: 0; }

/* The fill itself is absolutely positioned inside each half */
.ms-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;                 /* default: grow to the right */
  height: 100%;
  border-radius: 6px;
  opacity: .85;
}

/* For the LEFT team: anchor the fill to the CENTER (right edge of left half)
   so it grows leftwards from the middle */
.ms-half--left .ms-fill {
  left: auto;
  right: 0;
}

/* Center tick */
.ms-mid {
  position: absolute;
  left: 50%;
  top: -6px;
  bottom: -6px;
  width: 2px;
  background: #dfe3ea;
  transform: translateX(-1px);
}
.ms-skel { padding:2px 0; }
.ms-skel-row { height:28px; border-radius:8px; background:linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 37%,#f1f5f9 63%); background-size:400% 100%; animation:ms-shimmer 1.2s infinite linear; }
@keyframes ms-shimmer { 0% {background-position: 100% 0} 100% {background-position: -100% 0} }

.ms-error { padding:10px 12px; color:#991b1b; background:#fee2e2; border:1px solid #fecaca; border-radius:8px; }

@media (max-width: 560px) {
  .ms-head {
    display: grid !important;
    grid-template-columns: minmax(0,1fr) minmax(0,1fr);
  }
}
`;
